'use client';

import React, { useState } from 'react';
import GenerateButton from '@/components/ui/GenerateButton';
import AutoGrowingTextarea from '@/components/ui/AutoGrowingTextarea';

interface ChoiceOptions {
    choices: string[];
    numberOfSelections: number;
    allowDuplicates: boolean;
}

function GenerateChoicePage() {
    const [options, setOptions] = useState<ChoiceOptions>({
        choices: ['Option 1', 'Option 2', 'Option 3'],
        numberOfSelections: 1,
        allowDuplicates: false,
    });

    const [selectedChoices, setSelectedChoices] = useState<string>('');

    const handleChoiceChange = (index: number, value: string) => {
        const newChoices = [...options.choices];
        newChoices[index] = value;
        setOptions({ ...options, choices: newChoices });
    };

    const addChoice = () => {
        setOptions({
            ...options,
            choices: [...options.choices, `Option ${options.choices.length + 1}`],
        });
    };

    const removeChoice = (index: number) => {
        const newChoices = options.choices.filter((_, i) => i !== index);
        setOptions({ ...options, choices: newChoices });
    };

    const handleOptionChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { name, value, type, checked } = event.target;
        setOptions(prevOptions => ({
            ...prevOptions,
            [name]: type === 'checkbox' ? checked : type === 'number' ? Number(value) : value,
        }));
    };

    const generateChoices = () => {
        if (options.numberOfSelections < 1 || options.numberOfSelections > options.choices.length) {
            return;
        }
        let availableChoices = [...options.choices];
        let selected: string[] = [];

        for (let i = 0; i < options.numberOfSelections; i++) {
            if (availableChoices.length === 0) break;

            const randomIndex = Math.floor(Math.random() * availableChoices.length);
            const choice = availableChoices[randomIndex];
            selected.push(choice);

            if (!options.allowDuplicates) {
                availableChoices = availableChoices.filter((_, index) => index !== randomIndex);
            }
        }

        setSelectedChoices(selected.join('\n'));
    };

    return (
        <div className="w-full p-4">
            <div className='flex-grow flex justify-center mb-6'>
                <h1 className="text-2xl font-bold">Random Choice Generator</h1>
            </div>
            <div className="flex flex-col md:flex-row gap-6">
                {/* Left Column - Configuration */}
                <div className="w-full md:w-1/2">
                    <div className="form-control mb-4">
                        <h2 className="text-xl font-semibold mb-2">Choices</h2>
                        {options.choices.map((choice, index) => (
                            <div key={index} className="flex items-center mb-2">
                                <input
                                    type="text"
                                    className="input input-bordered w-full mr-2"
                                    value={choice}
                                    onChange={(e) => handleChoiceChange(index, e.target.value)}
                                />
                                <button
                                    className="btn btn-error btn-sm"
                                    onClick={() => removeChoice(index)}
                                >
                                    Remove
                                </button>
                            </div>
                        ))}
                        <button
                            className="btn btn-primary btn-sm mt-2"
                            onClick={addChoice}
                        >
                            Add Choice
                        </button>
                    </div>
                    <div className="form-control mb-4">
                        <label className="label">
                            <span className="label-text">Number of Selections (1-{options.choices.length})</span>
                        </label>
                        <input
                            type="number"
                            className={`input input-bordered w-full ${options.numberOfSelections < 1 || options.numberOfSelections > options.choices.length ? 'input-error' : ''}`}
                            name="numberOfSelections"
                            min="1"
                            max={options.choices.length}
                            value={options.numberOfSelections}
                            onChange={handleOptionChange}
                        />
                    </div>
                    <div className="form-control mb-4">
                        <label className="label cursor-pointer justify-start">
                            <input
                                type="checkbox"
                                className="checkbox checkbox-sm mr-2"
                                name="allowDuplicates"
                                checked={options.allowDuplicates}
                                onChange={handleOptionChange}
                            />
                            <span className="label-text">Allow Duplicates</span>
                        </label>
                    </div>
                    <GenerateButton generatePasswords={generateChoices} />
                </div>

                {/* Right Column - Generated Choices */}
                <div className="w-full md:w-1/2">
                    <h2 className="text-xl font-semibold mb-2">Selected Choice(s)</h2>
                    <AutoGrowingTextarea
                        value={selectedChoices}
                        minHeight={128}
                        maxHeight={512}
                    />
                </div>
            </div>
        </div>
    );
}

export default GenerateChoicePage;